
import { useNavigate } from 'react-router-dom';
import { Assessment } from '@/types/assessment';
import { useAssessmentProgress } from '@/hooks/useAssessmentProgress';
import { useAssessmentResponses } from '@/hooks/useAssessmentResponses';
import { 
  getFreeCategoriesWithQuestions, 
  calculateProgressPercentage,
  isQuestionFullyAnswered 
} from '@/utils/assessmentHelpers';
import AssessmentLayout from '@/components/assessment-portal/AssessmentLayout';
import AssessmentProgress from '@/components/assessment-portal/AssessmentProgress';
import AssessmentContent from '@/components/assessment-portal/AssessmentContent';

interface FreeAssessmentProps {
  assessment: Assessment;
}

const FreeAssessment = ({ assessment }: FreeAssessmentProps) => {
  const navigate = useNavigate();

  // Before any hooks read from localStorage, detect a previously completed
  // attempt for this assessment and wipe its cached progress + responses so
  // reloading the link starts a brand-new attempt (no stale green bubbles).
  if (typeof window !== 'undefined' && assessment?.id) {
    try {
      const progressKey = `assessment-progress-${assessment.id}`;
      const raw = localStorage.getItem(progressKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.step === 'complete') {
          localStorage.removeItem(progressKey);
          localStorage.removeItem(`assessment_${assessment.id}_responses`);
        }
      }
    } catch {
      // ignore
    }
  }

  // Get categories with only free questions
  const sortedCategories = getFreeCategoriesWithQuestions(assessment);

  
  // Use our custom hooks for assessment progress and responses
  const {
    currentStep,
    currentCategoryIndex,
    currentQuestionIndex,
    currentCategory,
    currentQuestion,
    personalInfo,
    setPersonalInfo,
    navigationProgressPercentage,
    handleStartAssessment,
    handleRestartAssessment,
    moveToNextQuestion,
    moveToPreviousQuestion,
    moveBackToQuestions,
    navigateToQuestion,
    completeAssessment
  } = useAssessmentProgress({ assessment, categories: sortedCategories });
  
  const {
    responses,
    responseCount,
    calculatedScores,
    handleResponseChange,
    canMoveForward,
    submitAssessment,
    isSubmitting,
    clearAllResponses
  } = useAssessmentResponses({ assessment, categories: sortedCategories });
  
  // Combined restart handler that clears both progress and responses
  const handleFullRestart = () => {
    clearAllResponses();
    handleRestartAssessment();
  };
  
  // Calculate total questions
  const totalQuestions = sortedCategories.reduce((sum, category) => sum + category.questions.length, 0);
  
  // Calculate completed questions count (both current and target answered)
  const completedQuestionsCount = Object.values(responses).filter(response => 
    isQuestionFullyAnswered(response)
  ).length;
  
  // Calculate progress percentage based on fully completed questions
  const progressPercentage = calculateProgressPercentage(totalQuestions, completedQuestionsCount);

  // Get current category questions count
  const currentCategoryQuestionsCount = currentCategory ? currentCategory.questions.length : 0;
  
  // Calculate how many questions are completed in the current category
  const currentCategoryCompletedCount = currentCategory ? 
    currentCategory.questions.filter(q => isQuestionFullyAnswered(responses[q.id])).length : 0;
  
  // Handle moving to next question with responses validation
  const handleMoveToNextQuestion = () => {
    moveToNextQuestion(responses);
  };
  
  // Handle personal info submission
  const handlePersonalInfoSubmit = async (info: typeof personalInfo) => {
    console.log('[FreeAssessment] Personal info submitted:', info);
    setPersonalInfo(info);
    
    console.log('[FreeAssessment] Before submission - responses available:', Object.keys(responses).length > 0);
    console.log('[FreeAssessment] Before submission - responses:', responses);
    
    try {
      const success = await submitAssessment(info);
      console.log('[FreeAssessment] Submission result:', success);
      
      if (success) {
        console.log('[FreeAssessment] Assessment completed. Final scores:', calculatedScores);
        console.log('[FreeAssessment] Assessment completed. Final responses for recommendations:', responses);
        completeAssessment();
      } else {
        console.error('[FreeAssessment] Assessment submission failed');
      }
    } catch (error) {
      console.error('[FreeAssessment] Error during submission:', error);
    }
  };

  // Handle direct navigation to specific question
  const handleNavigateToQuestion = (categoryIndex: number, questionIndex: number) => {
    navigateToQuestion(categoryIndex, questionIndex);
  };

  return (
    <AssessmentLayout 
      assessment={assessment}
      categories={sortedCategories}
      currentCategoryIndex={currentStep === 'questions' ? currentCategoryIndex : -1}
      currentStep={currentStep}
      currentQuestionIndex={currentQuestionIndex}
      responses={responses}
      onNavigateToQuestion={handleNavigateToQuestion}
    >
      {currentStep === 'questions' && (
        <AssessmentProgress 
          percentage={progressPercentage}
          currentCategory={currentCategory?.name}
          currentQuestionIndex={currentQuestionIndex >= 0 ? currentQuestionIndex : undefined}
          totalQuestions={currentCategoryQuestionsCount}
          completedQuestions={currentCategoryCompletedCount}
        />
      )}
      
      <div className={currentStep !== 'welcome' ? "mt-4" : ""}>
        <AssessmentContent
          currentStep={currentStep}
          assessment={assessment}
          categories={sortedCategories}
          currentCategoryIndex={currentCategoryIndex}
          currentQuestionIndex={currentQuestionIndex}
          responses={responses}
          scoresByCategory={calculatedScores}
          personalInfo={personalInfo}
          onStart={handleStartAssessment}
          onRestart={handleFullRestart}
          onExit={() => navigate('/')}
          onBack={currentStep === 'personalInfo' ? moveBackToQuestions : moveToPreviousQuestion}
          onNext={handleMoveToNextQuestion}
          onResponseChange={handleResponseChange}
          onPersonalInfoSubmit={handlePersonalInfoSubmit}
          canMoveForward={currentQuestion ? canMoveForward(currentQuestion) : true}
          isSubmitting={isSubmitting}
        />
      </div>
    </AssessmentLayout>
  );
};

export default FreeAssessment;
